<template>
  <div class="result-expandable">
    <div
      class="headcontent"
      role="button"
      tabindex="0"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      :aria-expanded="props.modelValue"
    >
      <div class="headcontent__container">
        <span class="headline">{{ title }}</span>
        <div class="info">
          {{ info }}
        </div>
      </div>
      <div class="expand-wrapper">
        <NavigationExpandMore
          class="svg-navigation-expand-more"
          :class="{ expanded: props.modelValue }"
          aria-hidden="true"
        />
      </div>
    </div>

    <div
      v-if="props.modelValue"
      class="content"
    >
      <div
        v-if="metadataItems.length > 0"
        class="vendor-design"
      >
        <slot
          name="filters"
          :items="metadataItems"
          :is-product-type="props.type === 'product'"
        >
          <div class="vendor-design__container">
            <div
              v-for="item in metadataItems"
              :key="item.label"
              class="vendor-design__col"
            >
              <div
                class="label"
                v-html="item.label"
              />
              <div
                class="value"
                v-html="item.value"
              />
            </div>
          </div>
          <div class="vendor-design__container">
            <div
              v-for="item in designPolymerFilters"
              :key="item.label"
              class="vendor-design__col"
            >
              <div
                class="label"
                v-html="item.label"
              />
              <div
                class="value"
                v-html="item.value"
              />
            </div>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue'
import NavigationExpandMore from '~/assets/icons/navigation_expand_more.svg?component'
import useI18nExtended from '~/composables/usei18nExtended'
import {useDimensionFormatter} from '~/composables/useDimensionFormatter'
import {useUserStore} from '@/store/user'

const i18n = useI18nExtended()
const {formatRangeDimension} = useDimensionFormatter()
const formatValue = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : String(v ?? '').trim())

const {showInchValues} = storeToRefs(useUserStore())

interface Props {
  modelValue?: boolean
  title?: string
  info?: string
  type?: 'product' | 'design'
  vendor?: string
  category?: string
  design?: string,
  compound?: string,
  itemNumber?: string,
  filters?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: '',
  info: '',
  type: 'design',
  vendor: '',
  category: '',
  design: '',
  compound: '',
  itemNumber: '',
  filters: () => []
})

const emits = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const excludeLabels = ['design', 'polymer_primary', 'productdesign', 'polymer', 'brand']
const supplierSortOrder = ['Supplier', 'Supplier Brand', 'Supplier Design', 'Supplier Material', 'Supplier Article Number', 'material1ShoreHardness', 'material1ShoreHardness', 'inner_diameter_rss', 'outer_diameter_rss', 'width_rss', 'width', 'housing_stick', 'width_of_lead_in_chamfer', 'radius1']

const getFilterLabel = (filter: any): string => {
  if (!filter) {
    return ''
  }
  let label = ''
  if (filter.label) {
    label = filter.label
  } else if (filter.displayName) {
    label = filter.displayName
    if (filter.displayNameShort && filter.displayNameShort !== filter.displayName) {
      label = `${filter.displayName} (${filter.displayNameShort})`
    }
  } else {
    label = filter.displayNameShort ||
      (filter.shortName ? i18n.tv(filter.shortName, 'filters') : '') ||
      filter.name ||
      ''
  }

  if (label && filter.unitOfMeasure) {
    const unit = showInchValues.value ? i18n.tv('inch', 'unitOfMeasurement') : i18n.tv('mm', 'unitOfMeasurement')
    return `${label} [${unit}]`
  }
  return label
}

const formatFilterValueTranslated = (filter: any, entity: string): string => {
  let filterTranslated = filter
  filterTranslated.values.forEach((value: any, index: number) => {
    if (value && value.value) {
      filterTranslated.values[index].label = i18n.tv(value.value, entity)
    }
  })

  return formatFilterValue(filterTranslated)
}

const formatFilterValue = (filter: any): string => {
  if (!filter) {
    return ''
  }
  const prefix = filter.customTranslatePrefix ?? 'filters'
  const val = filter.activeValues ?? filter.values

  if (!val) {
    return ''
  }

  if (Array.isArray(val)) {
    return val
      .map((v: any) => v?.label || v?.value || v)
      .filter(Boolean)
      .join(', ')
  }

  if (typeof val === 'object') {
    if (val.from !== undefined || val.start !== undefined || val.end !== undefined) {
      return formatRangeDimension(val, filter.unitOfMeasure, showInchValues.value ? 'inch' : 'mm')
    }
    if (filter.type === 'Float_min' || filter.type === 'Float_max') {
      return formatValue(val.value)
    }
    if (val.value !== undefined) {
      return String(val.value)
    }
  }

  return filter.translateLabel ? i18n.tv(val, prefix) : String(val)
}

const isDesignFilter = (filter: any): boolean => {
  return filter.shortName?.toLowerCase() === 'productdesign' || filter.shortName?.toLowerCase() === 'design'
}

const designPolymerFilters = computed(() => {
  const items = [...props.filters].filter((filter: any) => excludeLabels.includes(filter.shortName?.toLowerCase()))

  return items.map((filter: any) => {
    const isDesign = isDesignFilter(filter)
    return {
      ...filter,
      label: isDesign ? i18n.tv('ownDesign', 'ccrs') : i18n.tv('fstPolymer', 'ccrs'),
      value: isDesign ? formatFilterValueTranslated(filter, 'categories') : formatFilterValue(filter)
    }
  }).filter((item: any) => item.value && item.value !== '-')
})

const metadataItems = computed(() => {
  const items: Array<{label: string; value: string}> = []
  const filters = [...props.filters]

  const addItem = (value: string, label: string) => {
    if (value) {
      items.push({label: i18n.tv(label, 'ccrs'), value})
    }
  }

  addItem(props.vendor, 'vendor')
  addItem(props.category, 'category')
  addItem(props.design, 'CompetitorDesign')
  addItem(props.compound, 'CompetitorCompound')
  if (props.type === 'product') {
    addItem(props.itemNumber, 'itemNumber')
  }

  // only show filters with non-empty values
  if (props.type === 'product' && Array.isArray(props.filters)) {
    filters
      .forEach((filter: any) => {
        if (filter?.isActive) {
          const val = filter.activeValues ?? filter.values
          if (!val) {
            return
          }
          const label = getFilterLabel(filter)
          const value = formatFilterValue(filter)
          if (label && value && value !== '-') {
            items.push({label, value})
          }
        }
      })
  }

  return items.filter((filter: any) => !excludeLabels.includes(filter.label?.toLowerCase()))
    .sort((a: any, b: any) => {
      const aIndex = supplierSortOrder.findIndex((key) => a.label?.toLowerCase().startsWith(key.toLowerCase()))
      const bIndex = supplierSortOrder.findIndex((key) => b.label?.toLowerCase().startsWith(key.toLowerCase()))
      return aIndex - bIndex
    })
})

const toggle = () => {
  emits('update:modelValue', !props.modelValue)
}

</script>
<style lang="scss" scoped>
.result-expandable {
	background-color: $color__fst__white;
	padding: 1rem;
	box-shadow: 0 0 0.375rem $color__fst__gray__29;

  .headcontent {
    display: flex;
    justify-content: space-between;

    .svg-navigation-expand-more {
      cursor: pointer;
      color: $color__primary;
      font-size: 1.5rem;
      transition: transform 0.35s ease-in-out;

      &.expanded {
        transform: rotate(180deg);
      }
    }

    &__container {
      display: flex;
      gap: 1rem;
      align-items: center;
      min-width: 0;
      width: 100%;

      .headline {
        font-size: 1rem;
        color: $color__primary;
        line-height: 1;
        flex: 0 0 auto;
      }

      .info {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .vendor-design {
    padding: .813rem 0 0;

    &__container {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1.5rem 3rem;
      align-items: start;

      &:first-child {
        margin-bottom: 1.5rem;
      }

      @include breakpoint-down($md) {
        grid-template-columns: 1fr;
        gap: 1rem;
      }
    }

    &__col {
      .label {
        font-weight: $base-text-font-weight-bold;
        color: $color__primary;
        margin-bottom: 0.5rem;
        font-size: $base-text-font-size;
      }

      .value {
        font-weight: $base-text-font-weight-light;
        font-size: $base-text-font-size;
      }
    }
  }
}

</style>
